import { useState, useEffect, useCallback } from 'react'
import { db } from '../lib/supabase'

export function useData() {
  const [callLogs, setCallLogs]     = useState([])
  const [pipeline, setPipeline]     = useState([])
  const [reminders, setReminders]   = useState([])
  const [dbContacts, setDbContacts] = useState([])
  const [tasks, setTasks]           = useState([])
  const [activities, setActivities] = useState([])
  const [diary, setDiary]           = useState([])
  const [todos, setTodos]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const [calls, pipe, rems, contacts, tsk, acts, dia, tds] = await Promise.all([
        db.getCallLogs(), db.getPipeline(), db.getReminders(), db.getDbContacts(),
        db.getTasks(), db.getActivities(), db.getDiary(), db.getTodos(),
      ])
      setCallLogs(calls)
      setPipeline(pipe)
      setReminders(rems)
      setDbContacts(contacts)
      setTasks(tsk)
      setActivities(acts)
      setDiary(dia)
      setTodos(tds)
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const addCallLog = async (log) => {
    const row = await db.addCallLog(log)
    setCallLogs(prev => [row, ...prev])
    if (log.follow_up_date) {
      const rem = await db.addReminder({
        agent: log.agent, prospect_name: log.prospect_name, company: log.company,
        follow_up_date: log.follow_up_date, follow_up_time: '09:00',
        priority: log.bant_score >= 3 ? 'high' : 'medium',
        notes: log.next_action || 'Follow up', done: false,
      })
      setReminders(prev => [...prev, rem].sort((a,b) => a.follow_up_date.localeCompare(b.follow_up_date)))
    }
    return row
  }
  const updateCallLog = async (id, updates) => {
    const row = await db.updateCallLog(id, updates)
    setCallLogs(prev => prev.map(r => r.id === id ? { ...r, ...(row || updates) } : r))
    return row
  }
  const deleteCallLog = async (id) => {
    await db.deleteCallLog(id)
    setCallLogs(prev => prev.filter(r => r.id !== id))
  }

  const addDeal = async (deal) => {
    const row = await db.addDeal(deal)
    setPipeline(prev => [row, ...prev])
    return row
  }
  const updateDealStage = async (id, stage) => {
    await db.updateDealStage(id, stage)
    setPipeline(prev => prev.map(r => r.id === id ? { ...r, stage } : r))
  }
  const updateDeal = async (id, updates) => {
    await db.updateDeal(id, updates)
    setPipeline(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r))
  }
  const deleteDeal = async (id) => {
    await db.deleteDeal(id)
    setPipeline(prev => prev.filter(r => r.id !== id))
  }

  const addReminder = async (reminder) => {
    const row = await db.addReminder(reminder)
    setReminders(prev => [...prev, row].sort((a,b) => a.follow_up_date.localeCompare(b.follow_up_date)))
    return row
  }
  const markReminderDone = async (id) => {
    await db.markReminderDone(id)
    setReminders(prev => prev.map(r => r.id === id ? { ...r, done: true } : r))
  }
  const deleteReminder = async (id) => {
    await db.deleteReminder(id)
    setReminders(prev => prev.filter(r => r.id !== id))
  }

  const addDbContact = async (contact) => {
    const row = await db.addDbContact(contact)
    setDbContacts(prev => [row, ...prev])
    return row
  }
  const addDbContactsBulk = async (contacts) => {
    const rows = await db.addDbContactsBulk(contacts)
    setDbContacts(prev => [...rows, ...prev])
    return rows
  }
  const updateDbContact = async (id, updates) => {
    await db.updateDbContact(id, updates)
    setDbContacts(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r))
  }
  const deleteDbContact = async (id) => {
    await db.deleteDbContact(id)
    setDbContacts(prev => prev.filter(r => r.id !== id))
  }

  // ── Tasks ────────────────────────────────────────────────────────────────
  const addTask = async (task) => {
    const row = await db.addTask(task)
    if (row) setTasks(prev => [...prev, row])
    return row
  }
  // Idempotent: skips insert if an equivalent open task already exists (dedupe).
  const addTaskIdempotent = async (task) => {
    const row = await db.addTaskIdempotent(task)
    if (row) setTasks(prev => [...prev, row])
    return row
  }
  const updateTask = async (id, updates) => {
    const row = await db.updateTask(id, updates)
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...(row || updates) } : t))
    return row
  }
  const completeTask = async (id) => {
    return updateTask(id, { status: 'completed', completed_at: new Date().toISOString() })
  }
  const cancelTask = async (id, reason) => {
    return updateTask(id, { status: 'cancelled', cancelled_reason: reason || null })
  }
  const deleteTask = async (id) => {
    await db.deleteTask(id)
    setTasks(prev => prev.filter(t => t.id !== id))
  }

  // ── Activities (append-only) ──────────────────────────────────────────────
  const addActivity = async (activity) => {
    const row = await db.addActivity(activity)
    if (row) setActivities(prev => [row, ...prev])
    return row
  }

  // ── Diary ─────────────────────────────────────────────────────────────────
  const addDiary = async (entry) => {
    const row = await db.addDiary(entry)
    if (row) setDiary(prev => [row, ...prev])
    return row
  }
  const updateDiary = async (id, updates) => {
    const row = await db.updateDiary(id, updates)
    setDiary(prev => prev.map(d => d.id === id ? { ...d, ...(row || updates) } : d))
    return row
  }
  const deleteDiary = async (id) => {
    await db.deleteDiary(id)
    setDiary(prev => prev.filter(d => d.id !== id))
  }

  // ── Personal to-do ──────────────────────────────────────────────────────
  const addTodo = async (todo) => {
    const row = await db.addTodo(todo)
    if (row) setTodos(prev => [row, ...prev])
    return row
  }
  const updateTodo = async (id, updates) => {
    const row = await db.updateTodo(id, updates)
    setTodos(prev => prev.map(t => t.id === id ? { ...t, ...(row || updates) } : t))
    return row
  }
  const deleteTodo = async (id) => {
    await db.deleteTodo(id)
    setTodos(prev => prev.filter(t => t.id !== id))
  }

  // ── Ownership transfer (superadmin) + cross-agent duplicate lookup ────────
  const transferDbContact = async (id, { from_agent, to_agent, transferred_by, note } = {}) => {
    await db.updateDbContact(id, { agent: to_agent })
    setDbContacts(prev => prev.map(r => r.id === id ? { ...r, agent: to_agent } : r))
    await db.addOwnershipTransfer({ record_id: id, from_agent: from_agent || null, to_agent, transferred_by: transferred_by || null, note: note || null })
  }
  const checkDuplicate = async (args) => db.findContactDuplicate(args)

  return {
    callLogs, pipeline, reminders, dbContacts, tasks, activities, diary, todos,
    loading, error, reload: load,
    addCallLog, updateCallLog, deleteCallLog,
    addDeal, updateDealStage, updateDeal, deleteDeal,
    addReminder, markReminderDone, deleteReminder,
    addDbContact, addDbContactsBulk, updateDbContact, deleteDbContact,
    addTask, addTaskIdempotent, updateTask, completeTask, cancelTask, deleteTask,
    addActivity,
    addDiary, updateDiary, deleteDiary,
    addTodo, updateTodo, deleteTodo,
    transferDbContact, checkDuplicate,
  }
}
